/**
 * **Layar dapur (KDS)** — K5, Fase 4. Shell ketiga di atas inti yang sama (K4).
 *
 * ### ⭐ Bentuknya ditentukan orang yang membacanya
 * Yang menatap layar ini sedang memegang penjepit, berdiri satu-dua meter jauhnya, di ruangan yang
 * panas dan berisik. Jadi: kartu besar, nama barang tebal, jumlah di depan nama, dan **tak ada
 * apa pun yang perlu digulir untuk dimengerti**. Tak ada harga sama sekali — dapur tak pernah
 * butuh tahu harga, dan angka yang tak dibutuhkan justru ikut dibaca mata.
 *
 * ### ⛔ Umur tiket datang dari SERVER
 * Tablet dapur murah dan jamnya sering meleset berjam-jam. Umur yang dihitung dari jam tablet akan
 * menyalakan alarm pada tiket yang baru masuk, atau — lebih berbahaya — mendiamkan yang sudah lama
 * menggantung. `ageSeconds` dihitung Postgres; layar hanya menambahkan detik yang berlalu sejak
 * pembacaan terakhir.
 *
 * ### ⛔⛔ Bump ganda ditolak SERVER, dan layar menghormati penolakannya
 * Dua juru masak menekan tiket teratas bersamaan. Yang kedua ditolak (`KDS-409-ALREADY_DONE`), dan
 * layarnya **memuat ulang** alih-alih diam — kalau penolakan itu ditelan, ia akan mengira tiket
 * berikutnya yang sudah naik ke atas itulah yang barusan ia selesaikan.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { CACHE_KEYS, cacheGet, cacheSet } from '../api/cache';
import { api, type KitchenTicket } from '../api/client';
import { enqueueBump, flushBumps, gagalTransport, pendingBumps } from '../api/kds-outbox';
import { openLive, type LiveState } from '../api/live';
import { staffSession } from '../api/session';

export interface KitchenShellProps {
  readonly outletCode: string;
  readonly onLoggedOut: () => void;
}

/**
 * ⚠️ **Cadangan terakhir**, dipakai HANYA bila ambang dari server belum tiba (detik pertama layar
 * menyala). Ambang yang sesungguhnya adalah DATA — disetel di brand, boleh ditimpa outlet (0043).
 *
 * ⛔ Jangan dijadikan "bawaan yang masuk akal" lalu dibiarkan: angka di sini masuk akal untuk satu
 * jenis dapur dan konyol untuk yang lain. Di warung mi, tiket 8 menit sudah bencana; di fine
 * dining, steak medium 15 menit itu normal.
 */
const FALLBACK = { warnSeconds: 8 * 60, lateSeconds: 15 * 60 } as const;

function ageClass(seconds: number, t: { warnSeconds: number; lateSeconds: number }): string {
  if (seconds >= t.lateSeconds) return 'late';
  if (seconds >= t.warnSeconds) return 'warn';
  return 'fresh';
}

/**
 * ⭐⭐ **Judul kartu = TANDA pesanan**, bukan nomor pesanan.
 *
 * ⛔ `UI/000123/1` tak berarti apa-apa bagi juru masak: ia tak pernah menyebutnya, tak pernah
 * mendengarnya, dan tak bisa memakainya untuk mengantar. Yang ia pakai adalah tanda yang sama yang
 * diucapkan kasir dan didengar tamu — **Meja 5** · **Antrean 7** · **Budi**.
 *
 * ⚠️ Nomor pesanan tetap jadi jaring terakhir: pesanan tanpa meja & tanpa tanda memang tak punya
 * nama lain, dan kartu tanpa judul sama sekali tak bisa ditunjuk siapa pun.
 */
function judulKartu(k: KitchenTicket): {
  key: string; label: string; kind: 'table' | 'queue' | 'name' | 'none';
} {
  if (k.tableCode !== null && k.tableCode !== undefined) {
    return { key: 'kds.tag.plain', label: k.tableCode, kind: 'table' };
  }
  const label = k.parkLabel ?? null;
  if (label !== null && label !== '') {
    if (k.parkKind === 'queue') return { key: 'kds.tag.plain', label, kind: 'queue' };
    if (k.parkKind === 'name') return { key: 'kds.tag.plain', label, kind: 'name' };
    return { key: 'kds.tag.plain', label, kind: 'name' };
  }
  /*
   * ⛔ Nomor bill sebagai judul adalah jalan buntu: ia tak pernah diucapkan kasir, tak pernah
   * didengar tamu, dan tak bisa dipakai mengantar apa pun. Sejak gerbang penanda berdiri
   * (`ORDER-409-NO_TAG`) ia praktis tak muncul lagi — tapi kartu lama tetap harus punya judul,
   * dan `kind: 'none'` membuat layar MENGATAKAN bahwa yang ini memang tak bertanda.
   */
  return { key: 'kds.tag.plain', label: k.orderNo, kind: 'none' };
}

/**
 * ⭐⭐⭐ **Bayangan keluar** — penegasan visual yang **TIDAK menahan** apa pun.
 *
 * ### ⛔ Kenapa bukan animasi biasa
 * Menganimasikan elemen aslinya berarti ia masih di layar, masih di dalam alur, dan masih bisa
 * diketuk selama animasinya berjalan. Di jam sibuk itu persis jendela untuk ketukan kedua — dan
 * ketukan kedua di sini berarti hidangan berikutnya dinyatakan keluar padahal belum dimasak.
 *
 * ⇒ Yang asli **lenyap seketika** (penghapusan optimistis, seperti sebelumnya). Yang bergerak cuma
 * KLONNYA: `position: fixed` di atas segalanya, `pointer-events: none`, di luar alur — jadi tata
 * letak di bawahnya sudah rapat sebelum bayangannya selesai memudar.
 *
 * ⚠️ `data-testid` dicopot dari klon beserta seluruh isinya. Tanpa itu ada DUA elemen dengan
 * testid yang sama selama beberapa ratus milidetik, dan Playwright menolak yang ganda — sebuah
 * hiasan yang menjatuhkan puluhan uji tanpa satu pun berkaitan dengan hiasannya.
 *
 * ⛔ Dilewati sama sekali saat pengguna minta gerak dikurangi: aturan global sudah mematikan
 * `animation`, jadi klonnya akan DIAM di tempat sampai jaring pengamannya mencabutnya — bayangan
 * beku yang menutupi kartu sungguhan, lebih buruk daripada tanpa animasi.
 */
function lepasBayangan(el: Element | null): void {
  if (el === null || !(el instanceof HTMLElement)) return;
  if (globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const r = el.getBoundingClientRect();
  const klon = el.cloneNode(true) as HTMLElement;
  klon.removeAttribute('data-testid');
  klon.removeAttribute('id');
  for (const n of klon.querySelectorAll('[data-testid], [id]')) {
    n.removeAttribute('data-testid');
    n.removeAttribute('id');
  }
  klon.classList.add('kds-hantu');
  klon.setAttribute('aria-hidden', 'true');
  klon.style.top = `${String(r.top)}px`;
  klon.style.left = `${String(r.left)}px`;
  klon.style.width = `${String(r.width)}px`;
  klon.style.height = `${String(r.height)}px`;
  document.body.appendChild(klon);

  const buang = (): void => { klon.remove(); };
  klon.addEventListener('animationend', buang, { once: true });
  /* ⚠️ Jaring pengaman: kalau animasinya tak pernah selesai (tab disembunyikan, GPU sibuk),
     bayangannya tetap harus pergi — kertas menempel di layar dapur tak bisa dijelaskan siapa pun. */
  setTimeout(buang, 700);
}

function formatAge(seconds: number): string {
  const m = Math.floor(seconds / 60);
  return m < 1 ? `${String(seconds)}s` : `${String(m)}m`;
}

/**
 * ⭐⭐⭐ **Berapa kartu muat di satu layar** — pilihan CHEF, bukan keputusan CSS (Imron, 2026-08-23).
 *
 * ### Kenapa ini setelan dan bukan `auto-fill`
 * Sebelumnya kisi memakai `auto-fill minmax(260px, 1fr)`: jumlah kolomnya ditentukan LEBAR layar,
 * dan tinggi kartunya bebas. Akibatnya dua dapur dengan tablet yang sama melihat susunan berbeda
 * tergantung panjang pesanannya, dan tak ada satu pun cara mengaturnya.
 *
 * Yang dibutuhkan dapur bukan "sebanyak yang muat" melainkan **sebanyak yang bisa dibaca dari jarak
 * berdiri**: warung kecil ingin 4 kartu besar, dapur hotel saat rame ingin 16 kartu kecil. Itu
 * keputusan orang yang berdiri di depannya, dan ia berubah antara jam sepi dan jam sibuk.
 *
 * ⚠️ Baris ikut ditentukan supaya kartu ke-N benar-benar MUAT tanpa menggulir — kisi yang
 * kolomnya diatur tapi barisnya bebas tetap mendorong kartu keempat ke bawah lipatan, dan kartu di
 * bawah lipatan di dapur sama saja dengan kartu yang tak ada.
 */
const DENSITY = Object.freeze([
  { n: 4, cols: 2, rows: 2 },
  { n: 6, cols: 3, rows: 2 },
  { n: 8, cols: 4, rows: 2 },
  { n: 12, cols: 4, rows: 3 },
  { n: 16, cols: 4, rows: 4 },
] as const);

/** ⛔ Milik PERANGKAT, bukan milik orang: tablet dapur dipakai bergantian sepanjang shift. */
const DENSITY_KEY = 'osbpos.kds.density';

function densityTersimpan(): (typeof DENSITY)[number] {
  const v = Number(localStorage.getItem(DENSITY_KEY) ?? '8');
  return DENSITY.find((d) => d.n === v) ?? DENSITY[2];
}

/**
 * ⭐⭐⭐ **Ukuran kartu terkecil yang masih bisa dibaca dari jarak berdiri.**
 *
 * ⛔ Bukan selera: kepadatan yang ditawarkan harus benar-benar MUAT. Sebelum ini pemilihnya selalu
 * menawarkan 16 kartu — termasuk di layar setinggi 390px, tempat satu kartu kebagian 90px dan yang
 * terlihat cuma judulnya. Setelan yang tak bisa ditepati adalah setelan yang berbohong; layar ini
 * lebih baik menawarkan tiga pilihan yang benar daripada lima yang dua di antaranya omong kosong.
 */
const MIN_CARD_W = 200;
const MIN_CARD_H = 130;

/**
 * ⭐⭐⭐ **Selebar HP ⇒ SATU kartu, digeser** (Imron, 2026-08-24).
 *
 * ⛔ Kisi 2×2 di layar 360px memberi tiap kartu ±170×140px — cukup untuk judul, tak cukup untuk
 * isinya. Yang terjadi bukan "kartu kecil" melainkan **kartu yang isinya harus digulir satu per
 * satu**, dan juru masak yang menggulir di dalam kartu kehilangan tempatnya begitu daftar disegarkan.
 *
 * ⇒ Di bawah ambang ini kisi diganti SATU kartu selebar layar + geser/tombol. Satu pesanan penuh,
 * terbaca sekali lihat, tanpa gulir sama sekali.
 *
 * ⚠️ Diukur dari KOTAK KISINYA, bukan dari `window.innerWidth`: yang menentukan muat atau tidak
 * adalah ruang yang benar-benar tersisa sesudah pita atas dan panel all-day, dan itu tak sama
 * dengan lebar jendela.
 */
const PHONE_MAX_W = 560;

/**
 * ⭐⭐ **Rona kertas dari TANDA pesanan** — bukan acak, bukan dari nomor tiket.
 *
 * Yang dicari mata juru masak di tumpukan kartu adalah "punya siapa". Rona yang diturunkan dari
 * tandanya membuat susulan meja yang sama (`sendRound` 2) datang dengan warna kertas yang SAMA,
 * jadi kedua kartunya langsung terbaca sepasang tanpa satu pun tulisan tambahan.
 *
 * ⛔ Jenis ikut dimasukkan ke kuncinya: MEJA 7 dan NAMA 7 wajib berbeda warna, karena nama tamu
 * boleh berupa angka dan dua kartu berwarna sama yang isinya berbeda justru menyesatkan.
 *
 * ⚠️ Ronanya IDENTITAS, bukan keadaan — jenuhnya rendah supaya tak berebut perhatian dengan pita
 * keadaan di tepi kiri dan latar merah tiket terlambat. Pola yang sama dipakai `--dept-hue`.
 *
 * ⛔⛔ MERAH, JINGGA, dan MERAH MUDA sengaja tak ada di daftar (rona 0–40 & 300–360). Ketiganya
 * adalah bahasa KEADAAN di layar ini — pita jingga "hampir telat", latar merah muda "TERLAMBAT".
 * Kertas merah muda milik seorang tamu bernama Budi terbaca persis seperti alarm, dan alarm yang
 * ternyata cuma warna kertas mengajari juru masak untuk berhenti mempercayainya.
 */
const NOTE_HUES = [48, 88, 150, 190, 225, 270] as const;

/**
 * ⭐⭐ **Miring sedikit** — dan justru sedikitnya yang penting.
 *
 * Kertas yang ditempel orang tak pernah lurus. Kemiringan di bawah satu derajat tak terbaca sebagai
 * "gambar bengkok" tapi cukup untuk memutus kesan KISI: dua belas kotak yang tepinya sejajar
 * terbaca sebagai satu tabel, dan itulah keluhan *"orderan berkumpul jadi satu"*.
 *
 * ⛔ Sudutnya diturunkan dari kunci yang sama dengan ronanya ⇒ **stabil**. Kartu yang sudut
 * miringnya berganti tiap kali layar digambar ulang (tiap detik, karena umur tiket berjalan) akan
 * terlihat bergetar — dan layar yang bergetar di ruangan panas membuat orang berhenti menatapnya.
 * ⚠️ Pada 0,6° kartu selebar 300px cuma bergeser ±3px di sudutnya — masih jauh di dalam celah 12px
 * antar-kartu, jadi tak ada yang saling menimpa.
 */
const NOTE_TILTS = ['-0.7deg', '0.5deg', '-0.35deg', '0.8deg', '-0.55deg', '0.4deg'] as const;

/** Kunci rona & kemiringan. ⚠️ Kartu tanpa tanda tetap punya kunci — nomor tiketnya. */
function noteKey(kind: string, label: string): number {
  const key = `${kind}:${label}`;
  let h = 0;
  for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) % 100003;
  return h;
}

function noteHue(kind: string, label: string): number {
  return NOTE_HUES[noteKey(kind, label) % NOTE_HUES.length] ?? NOTE_HUES[0];
}

function noteTilt(kind: string, label: string): string {
  return NOTE_TILTS[noteKey(kind, label) % NOTE_TILTS.length] ?? '0deg';
}

/**
 * Kepadatan yang benar-benar muat di kotak seukuran ini. ⚠️ Selalu mengembalikan minimal satu
 * pilihan — layar yang tak menawarkan apa pun lebih buruk daripada satu pilihan yang sesak.
 */
function densityMuat(w: number, h: number): readonly (typeof DENSITY)[number][] {
  const muat = DENSITY.filter((d) => w / d.cols >= MIN_CARD_W && h / d.rows >= MIN_CARD_H);
  return muat.length > 0 ? muat : [DENSITY[0]];
}

export function KitchenShell(p: KitchenShellProps) {
  const { t } = useTranslation();
  const [stations, setStations] = useState<readonly { code: string; name: string }[]>([]);
  const [station, setStation] = useState<string | undefined>(undefined);
  const [tickets, setTickets] = useState<readonly KitchenTicket[]>([]);
  /**
   * ⭐⭐⭐ **"All day"** — total per item lintas seluruh tiket terbuka.
   *
   * `listTickets` menjawab *"tiket ini isinya apa"*. Yang chef butuhkan saat rame justru pertanyaan
   * lain: *"ayam bakar totalnya berapa SEKARANG"*. Tanpa jawabannya ia memasak per tiket — kompor
   * menyala tujuh kali untuk barang yang sama.
   */
  const [allDay, setAllDay] = useState<readonly {
    productCode: string; productName: string; qty: string; ticketCount: number;
    oldestSeconds: number;
  }[]>([]);
  /**
   * ⭐⭐ Panel all-day **TERTUTUP bawaannya** (Imron, 2026-08-23).
   *
   * ⛔ Terbuka, ia berdiri di ATAS kartu dan mendorong tiketnya ke bawah lipatan — dan yang
   * ditatap juru masak jadi satu daftar gabungan ("8× ayam bakar"), bukan pesanannya. Itulah yang
   * dilaporkan sebagai *"orderan berkumpul jadi satu"*.
   *
   * ⚠️ Ia tetap ADA dan tetap satu ketukan: yang wajib (tiket) tak boleh tertutup yang membantu
   * (total), tapi yang membantu juga tak boleh dibuang.
   */
  const [allDayOpen, setAllDayOpen] = useState(false);
  /** ⭐ Kepadatan kisi — dibaca dari perangkat, bukan dari sesi staf. */
  const [density, setDensity] = useState(densityTersimpan);
  /** Item yang sedang ditanyai "tandai habis?". `null` = tak ada dialog. */
  const [habisFor, setHabisFor] = useState<{ code: string; name: string } | null>(null);
  const [failed, setFailed] = useState(false);
  const [liveState, setLiveState] = useState<LiveState>('connecting');
  /** Detik sejak pembacaan terakhir — supaya umur tetap berjalan tanpa membebani server. */
  const [drift, setDrift] = useState(0);
  const [thresholds, setThresholds] =
    useState<{ warnSeconds: number; lateSeconds: number }>(FALLBACK);
  /**
   * ⭐⭐⭐ **Kapan tiket yang sedang tergambar terakhir dijawab SERVER** — `null` = baru saja.
   *
   * ⛔ Ini bukan hiasan. Begitu layar boleh menampilkan salinan, ia wajib bisa menjawab *"ini data
   * kapan?"* — tiket basi yang tak mengaku basi membuat dapur memasak pesanan yang sudah
   * dibatalkan, dan tak ada satu pun tanda bahwa ada yang salah.
   */
  const [salinanSejak, setSalinanSejak] = useState<string | null>(null);
  /** Bump yang belum diakui server. ⭐ Terlihat di pita atas — antrean tak kasatmata tak dipercaya. */
  const [tertahan, setTertahan] = useState(pendingBumps());
  const busy = useRef(false);
  /**
   * ⚠️ Cermin `tickets` untuk dibaca dari dalam `load` — `load` di-`useCallback` tanpa `tickets`
   * di dependensinya (menambahkannya akan membuatnya dirakit ulang tiap pembacaan, lalu langganan
   * kanal dorong ikut dibongkar-pasang tiap beberapa detik).
   */
  const adaTiket = useRef(false);

  /**
   * ⭐⭐⭐ **Kotak kisi yang SEBENARNYA**, diukur — bukan ditebak dari `window.innerHeight`.
   *
   * ⛔ Menebaknya berarti mengurangi tinggi pita atas, panel all-day, dan banner "data lama" dengan
   * angka yang ditulis tangan — tiga angka yang akan menyimpang diam-diam begitu salah satunya
   * berubah tinggi, dan menyimpangnya terlihat sebagai kartu yang terpotong, bukan sebagai galat.
   *
   * ⚠️ Nilai awalnya tetap ditebak dari jendela supaya gambar PERTAMA sudah benar: kisi yang
   * sekejap tampil 4 kolom di HP lalu berkedip jadi satu kartu terbaca sebagai layar rusak.
   */
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [box, setBox] = useState<{ w: number; h: number }>(() => ({
    w: Math.max(0, globalThis.innerWidth - 24),
    h: Math.max(0, globalThis.innerHeight - 110),
  }));

  useEffect(() => {
    const el = gridRef.current;
    if (el === null) return;
    const ukur = (): void => { setBox({ w: el.clientWidth, h: el.clientHeight }); };
    ukur();
    /* ⭐ `ResizeObserver`, bukan `resize` jendela: tablet dapur diputar, papan ketik naik-turun, dan
       panel all-day dibuka-tutup — tiga hal yang mengubah kotak ini tanpa mengubah ukuran jendela. */
    const ro = new ResizeObserver(ukur);
    ro.observe(el);
    return () => { ro.disconnect(); };
  }, []);

  /** ⭐ Selebar HP ⇒ satu kartu. Keputusan ini yang mematikan kisi, pemilih kepadatan, dan gulir. */
  const single = box.w > 0 && box.w < PHONE_MAX_W;

  const opsiDensity = useMemo(() => densityMuat(box.w, box.h), [box.w, box.h]);
  /**
   * ⚠️ Pilihan chef DISIMPAN utuh; yang dijepit cuma yang BERLAKU sekarang. Tablet yang diputar ke
   * potret lalu kembali ke lanskap wajib mendapatkan kembali 16 kartunya — menimpa setelannya saat
   * diputar berarti dia harus menyetel ulang tiap kali mengangkat tabletnya.
   */
  const efektif = opsiDensity.some((d) => d.n === density.n)
    ? density
    : (opsiDensity[opsiDensity.length - 1] ?? DENSITY[0]);

  /**
   * ⭐⭐ **Kartu yang sedang ditatap dilacak lewat ID tiketnya, bukan lewat nomor urut.**
   *
   * ⛔ Nomor urut teleport: satu tiket baru masuk di urutan atas (atau satu di-bump juru masak lain)
   * dan kartu yang sedang dibaca berganti jadi pesanan lain — di tangan orang yang sedang menuang
   * kuah. Dengan ID, daftar boleh berubah di bawahnya tanpa memindahkan pandangannya.
   *
   * ⚠️ Kalau tiketnya memang LENYAP (dia sendiri yang membumpnya), barulah posisi terakhir dipakai:
   * yang benar sesudah "jadi" adalah pesanan berikutnya di tempat yang sama, bukan kembali ke awal.
   */
  const [fokusId, setFokusId] = useState<string | null>(null);
  const posisiRef = useRef(0);
  const idx = (() => {
    if (tickets.length === 0) return 0;
    const i = fokusId === null ? -1 : tickets.findIndex((k) => k.ticketId === fokusId);
    return i >= 0 ? i : Math.min(posisiRef.current, tickets.length - 1);
  })();
  useEffect(() => { posisiRef.current = idx; }, [idx]);

  const geser = useCallback((arah: number): void => {
    setFokusId(() => {
      const n = tickets.length;
      if (n === 0) return null;
      const j = Math.min(n - 1, Math.max(0, posisiRef.current + arah));
      return tickets[j]?.ticketId ?? null;
    });
  }, [tickets]);

  /**
   * ⛔⛔ **Geser yang berakhir di atas tombol "Jadi" TIDAK BOLEH mem-bump.**
   *
   * `pointerup` diikuti `click` pada elemen di bawah jari. Tanpa penjaga ini, satu gesekan yang
   * kebetulan berhenti di footer kartu menyatakan hidangan sudah keluar — kesalahan yang cuma
   * ketahuan dari tamu yang tak kunjung dapat makanan.
   */
  const geserBaru = useRef(false);
  const tekanDi = useRef<{ x: number; y: number } | null>(null);

  /** Yang digambar: seluruh tiket (kisi), atau SATU yang sedang ditatap (selebar HP). */
  const tampil = single ? tickets.slice(idx, idx + 1) : tickets;

  useEffect(() => {
    void (async () => {
      try {
        const list = await api.kitchenStations(p.outletCode);
        setStations(list);
        setStation((s) => s ?? list[0]?.code);
        cacheSet(CACHE_KEYS.kitchenStations(p.outletCode), list);
      } catch {
        /*
         * ⛔ Daftar stasiun ikut disalin — tanpa itu tablet yang menyala luring tak punya stasiun
         * terpilih sama sekali, dan cache tiket (yang berkunci stasiun) tak bisa dibaca. Layarnya
         * kosong bukan karena tiketnya hilang, melainkan karena tak tahu harus mencari yang mana.
         */
        const salinan =
          cacheGet<readonly { code: string; name: string }[]>(
            CACHE_KEYS.kitchenStations(p.outletCode),
          );
        if (salinan !== undefined) {
          setStations(salinan.value);
          setStation((s) => s ?? salinan.value[0]?.code);
        }
        setFailed(true);
      }
    })();
  }, [p.outletCode]);

  const load = useCallback(async () => {
    if (station === undefined) return;
    try {
      /*
       * ⭐ Ambang ikut dibaca ULANG tiap muat. Layar dapur menyala berjam-jam tanpa pernah
       * di-refresh; ambang yang hanya dibaca saat mount berarti manajer yang menyetel ulang di
       * portal tak berpengaruh apa pun sampai ada yang mematikan tablet.
       */
      const [list, amb] = await Promise.all([
        api.kitchenTickets(p.outletCode, station),
        api.kitchenThresholds(p.outletCode),
      ]);
      setTickets(list);
      setThresholds({ warnSeconds: amb.warnSeconds, lateSeconds: amb.lateSeconds });
      setDrift(0);
      setFailed(false);
      /* ⭐ Jawaban server yang BARU SAJA tiba ⇒ tak ada lagi yang perlu disebut basi. */
      setSalinanSejak(null);
      cacheSet(CACHE_KEYS.kitchenTickets(p.outletCode, station), list);
      /*
       * ⭐⭐ Jaringan jelas hidup (permintaan barusan berhasil) ⇒ dorong bump yang tertahan.
       * ⛔ Di sini, bukan di timer tersendiri: yang menandai jaringan pulih adalah permintaan yang
       * berhasil, dan timer kedua cuma menambah tempat untuk lupa.
       */
      if (pendingBumps() > 0) {
        await flushBumps();
        setTertahan(pendingBumps());
      }
    } catch {
      /*
       * ⚠️ Kegagalan muat TIDAK mengosongkan layar. Dapur yang tiba-tiba bersih akan diartikan
       * "semua sudah keluar" — dan tak ada yang memasak apa pun sampai ada tamu yang bertanya.
       */
      setFailed(true);
      /*
       * ⭐⭐⭐ **Salinan dipakai HANYA saat layar belum punya apa-apa** — yaitu tablet yang baru
       * dimuat ulang / baru menyala dalam keadaan terputus.
       *
       * ⛔ Kalau layar sudah menggambar tiket dari server, jangan ditimpa salinan yang lebih tua:
       * yang tergambar sekarang lebih baru daripada apa pun yang tersimpan, dan menimpanya berarti
       * memundurkan dapur satu putaran.
       */
      if (!adaTiket.current) {
        const salinan =
          cacheGet<readonly KitchenTicket[]>(CACHE_KEYS.kitchenTickets(p.outletCode, station));
        if (salinan !== undefined) {
          setTickets(salinan.value);
          setSalinanSejak(salinan.savedAt);
        }
      }
    }

    /*
     * ⛔⛔ **All-day ditarik TERPISAH, dan gagalnya tak menjatuhkan apa pun.**
     *
     * Ia sempat ikut di `Promise.all` bersama tiket — dan itu salah dengan cara yang paling
     * berbahaya di ruangan ini: satu permintaan bantu yang gagal (endpointnya belum ada di
     * mini-PC lama, jaringan tersendat, stasiunnya baru dibuat) membuat SELURUH daftar tiket
     * lenyap. Dapur yang tiba-tiba bersih akan diartikan "semua sudah keluar", dan tak ada yang
     * memasak apa pun sampai ada tamu bertanya.
     *
     * ⚠️ Tiket = wajib; all-day = alat bantu. Yang wajib tak boleh bergantung pada yang membantu.
     */
    try {
      const ad = await api.kitchenAllDay(p.outletCode, station);
      setAllDay(ad.items);
    } catch {
      /* Panelnya sekadar tak muncul. Tiketnya — yang menentukan apa yang dimasak — tetap utuh. */
      setAllDay([]);
    }
  }, [p.outletCode, station]);

  useEffect(() => {
    void load();
    const staf = staffSession();
    if (staf === undefined) return;
    const channel = openLive({
      outletCode: p.outletCode,
      staffToken: staf.token,
      onNudge: (kind) => { if (kind === undefined || kind === 'kitchen') void load(); },
      onState: setLiveState,
    });
    return () => { channel.close(); };
  }, [load, p.outletCode]);

  useEffect(() => { adaTiket.current = tickets.length > 0; }, [tickets]);

  /**
   * ⭐ Umur salinan, dalam detik — dan ia BERJALAN: jam dinding `drift` menggambar ulang komponen
   * ini tiap detik, jadi `Date.now()` di bawah selalu baru. Banner yang membeku di "3m" selama
   * seperempat jam justru meyakinkan orang bahwa datanya masih segar.
   *
   * ⚠️ Dihitung dari jam TABLET — dan di sini itu benar justru karena tablet dapur jamnya suka
   * meleset: `savedAt` dan "sekarang" datang dari jam yang SAMA, jadi selisihnya tetap tepat walau
   * jamnya sendiri salah berjam-jam. (Umur TIKET beda soal — itu tetap dari server.)
   */
  const umurSalinan =
    salinanSejak === null
      ? 0
      : Math.max(0, Math.floor((Date.now() - new Date(salinanSejak).getTime()) / 1000));

  /**
   * ⭐⭐ Jaringan pulih ⇒ dorong antrean SEKARANG, tak menunggu putaran pembacaan berikutnya.
   *
   * ⚠️ `online` peramban hanya berarti "ada antarmuka jaringan", bukan "server terjangkau" — jadi
   * ia pemicu, bukan bukti. Yang membuktikan tetap permintaan yang berhasil di `load`.
   */
  useEffect(() => {
    const onOnline = () => {
      void flushBumps().then(() => { setTertahan(pendingBumps()); void load(); });
    };
    globalThis.addEventListener('online', onOnline);
    return () => { globalThis.removeEventListener('online', onOnline); };
  }, [load]);

  /* Jam dinding kecil: menambah detik ke umur tanpa menembak server tiap detik. */
  useEffect(() => {
    const id = setInterval(() => { setDrift((d) => d + 1); }, 1000);
    return () => { clearInterval(id); };
  }, []);

  /**
   * ⭐⭐ **Menandai habis DARI DAPUR.**
   *
   * ⛔ Sampai hari ini tandanya cuma bisa dipasang dari layar kasir — padahal yang tahu ayamnya
   * habis adalah orang yang berdiri di depan wajan. Alurnya jadi: dapur teriak → kasir mencari
   * kartunya → tekan-tahan; dan selama teriakan itu belum sampai, till sebelah terus menjualnya.
   *
   * ⚠️ Gagal DIKATAKAN, tak ditelan diam-diam: tanda habis yang dikira terpasang padahal tidak
   * lebih berbahaya daripada tak menekan apa pun.
   */
  const tandaiHabis = useCallback(async (productCode: string): Promise<void> => {
    try {
      await api.setSoldOut(p.outletCode, productCode, true);
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, [p.outletCode]);

  /**
   * ⭐⭐⭐ **Kirim bump; kalau jaringannya yang gagal, ANTREKAN.**
   *
   * ⛔ Sebelum ini kegagalan jaringan berarti bumpnya **lenyap**: tiketnya sudah disingkirkan dari
   * layar, permintaannya mati di tengah jalan, dan pembacaan berikutnya mengembalikannya. Hidangan
   * yang sudah diantar dimasak dua kali — dan di jam sibuk itu tak terlihat sebagai galat, cuma
   * sebagai dapur yang mendadak kacau.
   *
   * ⚠️ Yang DITOLAK server (4xx) sengaja tak diantrekan: jawabannya tegas dan takkan berubah.
   * Kalau bumpnya memang tak berlaku, tiketnya muncul lagi pada pembacaan berikutnya — dan itulah
   * umpan baliknya, di tempat yang memang ditatap juru masak.
   */
  const kirimBump = useCallback(async (
    jenis: 'ticket' | 'line',
    id: string,
    orderNo?: string,
  ): Promise<void> => {
    try {
      await (jenis === 'ticket' ? api.kitchenBump(id) : api.kitchenBumpLine(id));
    } catch (e) {
      if (gagalTransport(e)) {
        enqueueBump({ jenis, id, ...(orderNo === undefined ? {} : { orderNo }) });
        setTertahan(pendingBumps());
      }
    }
  }, []);

  /**
   * `start` = tiket mulai dimasak · `bump` = tiket keluar.
   *
   * ⚠️ Hanya **`bump`** yang diantrekan saat jaringan mati. `start` yang tak sampai cuma membuat
   * tiket tetap tampil "belum mulai" — menjengkelkan, tak berbahaya. Bump yang tak sampai membuat
   * hidangan yang sudah diantar dimasak dua kali. Antrean cuma untuk yang kedua.
   */
  async function act(ticketId: string, aksi: 'start' | 'bump', orderNo: string): Promise<void> {
    if (busy.current) return;
    busy.current = true;
    /*
     * ⭐ Tiketnya disingkirkan dari layar SEBELUM server menjawab — juru masak sudah beralih ke
     * tiket berikutnya sebelum jaringan sempat membalas, dan tiket yang masih tergambar akan
     * ditekan lagi.
     */
    if (aksi === 'bump') {
      lepasBayangan(document.querySelector(`[data-testid="kds-ticket-${orderNo}"]`));
      setTickets((list) => list.filter((k) => k.ticketId !== ticketId));
    }
    try {
      if (aksi === 'bump') await kirimBump('ticket', ticketId, orderNo);
      else await api.kitchenStart(ticketId).catch(() => undefined);
    } finally {
      busy.current = false;
      // ⛔ Selalu baca ulang — termasuk saat ditolak; penolakan berarti layar ini tertinggal.
      await load();
    }
  }

  /**
   * ⭐ Menandai SATU hidangan keluar. Barisnya disingkirkan lebih dulu — alasan yang sama dengan
   * `act`: juru masak sudah beralih sebelum jaringan membalas, dan baris yang masih tergambar akan
   * ditekan lagi.
   *
   * ⭐⭐⭐ **Hidangan TERAKHIR menutup memonya sendiri** (Imron, 2026-08-24: *"item terakhir auto
   * remove sticky notes tidak perlu tombol lagi"*).
   *
   * ⛔ Kartu kosong yang masih menyisakan tombol "Jadi" menuntut satu ketukan yang **tak berarti
   * apa-apa** — tiketnya sudah selesai, servernya sudah tahu (`bumpLine` menutupnya begitu baris
   * terakhir habis), dan yang tersisa cuma pekerjaan tangan. Ketukan yang tak berarti apa-apa
   * kelak dilakukan tanpa melihat, dan kebiasaan itulah yang nanti menghapus kartu yang keliru.
   *
   * ⚠️ **Kepala paket tak ikut dihitung** — persis seperti hitungan server (`NOT is_header`):
   * tiket yang tinggal berisi kepala set menu saja memang sudah selesai, karena kepala bukan
   * hidangan dan tak pernah bisa diketuk siapa pun.
   */
  async function bumpLine(ticketId: string, ticketLineId: string): Promise<void> {
    if (busy.current) return;
    busy.current = true;
    setTickets((list) => list.flatMap((k) => {
      if (k.ticketId !== ticketId) return [k];
      const sisa = k.lines.filter((l) => l.id !== ticketLineId);
      if (sisa.every((l) => l.isHeader)) return [];
      return [{ ...k, lines: sisa }];
    }));
    try {
      await kirimBump('line', ticketLineId);
    } finally {
      busy.current = false;
      await load();
    }
  }

  return (
    <div className="kds" data-testid="kds">
      <header className="kds-bar">
        <select
          data-testid="kds-station"
          value={station ?? ''}
          onChange={(e) => { setStation(e.target.value); }}
          aria-label={t('kds.station')}
        >
          {stations.map((s) => (
            <option key={s.code} value={s.code}>{s.name}</option>
          ))}
        </select>
        <span className="kds-count" data-testid="kds-count">
          {t('kds.pending', { count: tickets.length })}
        </span>
        {liveState === 'degraded' ? (
          <span className="warn" data-testid="kds-degraded">{t('kds.degraded')}</span>
        ) : null}
        {/* ⭐ Antrean yang tak kasatmata tak dipercaya — angkanya disebut, di tempat yang ditatap. */}
        {tertahan > 0 ? (
          <span className="warn" data-testid="kds-queued">
            {t('kds.queued', { n: tertahan })}
          </span>
        ) : null}
        {/*
          ⭐⭐ Pemilih kepadatan. ⛔ Di pita atas bersama pemilih stasiun, bukan di menu tersembunyi:
          ia diubah saat jam berganti dari sepi ke sibuk — beberapa kali sehari, oleh orang yang
          tangannya sedang kotor.
        */}
        {/*
          ⛔ Di layar selebar HP pemilih ini DIHILANGKAN, bukan dikecilkan: di sana hanya ada satu
          susunan yang mungkin, dan pemilih yang semua pilihannya menghasilkan tampilan yang sama
          adalah tombol yang berbohong.
        */}
        {!single && (
          <select
            data-testid="kds-density"
            className="kds-density"
            value={efektif.n}
            aria-label={t('kds.density')}
            onChange={(e) => {
              const d = DENSITY.find((x) => x.n === Number(e.target.value)) ?? DENSITY[2];
              setDensity(d);
              /* ⚠️ Disimpan langsung: layar dapur menyala berjam-jam dan jarang dimuat ulang; setelan
                 yang cuma hidup di memori akan hilang tepat saat tabletnya di-restart malam hari. */
              try { localStorage.setItem(DENSITY_KEY, String(d.n)); } catch { /* mode privat */ }
            }}
          >
            {/* ⭐ Hanya yang MUAT — lihat `densityMuat`. */}
            {opsiDensity.map((d) => (
              <option key={d.n} value={d.n}>{t('kds.densityOption', { n: d.n })}</option>
            ))}
          </select>
        )}
        <button type="button" className="ghost" onClick={p.onLoggedOut}>{t('common.logout')}</button>
      </header>

      {/*
        ⛔⛔ Saat yang tergambar adalah SALINAN, yang dikatakan bukan "gagal memuat" melainkan
        **umur datanya**. "Gagal memuat" di atas daftar tiket yang penuh terbaca sebagai gangguan
        kecil; yang sebenarnya terjadi adalah dapur sedang menatap keadaan beberapa menit lalu.
      */}
      {salinanSejak !== null ? (
        <p className="error" data-testid="kds-stale">
          {t('kds.stale', { age: formatAge(umurSalinan) })}
        </p>
      ) : failed ? (
        <p className="error" data-testid="kds-error">{t('kds.loadFailed')}</p>
      ) : null}

      {tickets.length === 0 && !failed ? (
        <p className="empty" data-testid="kds-empty">{t('kds.empty')}</p>
      ) : null}

      {/*
        ⭐⭐⭐ Panel ALL DAY. ⛔ Di ATAS kisi tiket, bukan di samping: yang dibaca chef sebelum
        menyalakan kompor adalah totalnya, dan yang harus dicari dengan menoleh akan berhenti
        dibaca pada jam yang justru paling membutuhkannya.
      */}
      {allDay.length > 0 && (
        <section className="kds-allday" data-testid="kds-allday">
          <button
            type="button"
            className="kds-allday-head"
            data-testid="kds-allday-toggle"
            aria-expanded={allDayOpen}
            onClick={() => { setAllDayOpen((v) => !v); }}
          >
            {t('kds.allDay.title')}
            <span className="kds-allday-count">{allDay.length}</span>
          </button>
          {allDayOpen && (
            <ul className="kds-allday-list">
              {allDay.map((it) => (
                <li key={it.productCode}>
                  <span className="kds-allday-qty">{Math.round(Number(it.qty))}×</span>
                  <span className="kds-allday-name">{it.productName}</span>
                  <span className="kds-allday-meta">
                    {t('kds.allDay.tickets', { count: it.ticketCount })}
                    {' · '}
                    {formatAge(it.oldestSeconds + drift)}
                  </span>
                  {/*
                    ⭐⭐ Tombol HABIS ADA DI SINI, bukan cuma di kasir. Yang tahu ayamnya habis
                    adalah dapur; alur lama ("dapur teriak → kasir mencari kartunya") membiarkan
                    till sebelah terus menjualnya selama teriakan itu belum sampai.
                  */}
                  <button
                    type="button"
                    className="kds-habis"
                    data-testid={`kds-habis-${it.productCode}`}
                    onClick={() => { setHabisFor({ code: it.productCode, name: it.productName }); }}
                  >
                    {t('kds.allDay.markSoldOut')}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {habisFor !== null && (
        <div className="dialog" role="dialog" data-testid="kds-habis-dialog">
          <h2>{habisFor.name}</h2>
          <div className="dialog-actions">
            <button type="button" data-testid="kds-habis-cancel" onClick={() => { setHabisFor(null); }}>
              {t('kds.allDay.cancel')}
            </button>
            <button
              type="button"
              className="action-primary"
              data-testid="kds-habis-confirm"
              onClick={() => { const it = habisFor; setHabisFor(null); void tandaiHabis(it.code); }}
            >
              {t('kds.allDay.markSoldOut')}
            </button>
          </div>
        </div>
      )}

      <div
        ref={gridRef}
        className={single ? 'kds-grid kds-single' : 'kds-grid'}
        data-testid="kds-grid"
        data-density={efektif.n}
        data-mode={single ? 'single' : 'grid'}
        style={{
          '--kds-cols': efektif.cols,
          '--kds-rows': efektif.rows,
        } as CSSProperties}
        /*
         * ⭐⭐ Geser kiri/kanan = kartu berikut/sebelumnya. Dipasang lewat **pointer**, bukan touch:
         * satu jalur yang sama melayani jari di tablet, stylus, dan tetikus di layar sentuh yang
         * dipakai dengan mouse — dan yang paling penting, ia bisa diuji.
         *
         * ⚠️ Gerakan MENDATAR saja yang dihitung: gulir vertikal di dalam kartu panjang tak boleh
         * ikut memindahkan halaman.
         */
        onPointerDown={(e) => {
          geserBaru.current = false;
          tekanDi.current = single ? { x: e.clientX, y: e.clientY } : null;
        }}
        onPointerUp={(e) => {
          const a = tekanDi.current;
          tekanDi.current = null;
          if (a === null) return;
          const dx = e.clientX - a.x;
          const dy = e.clientY - a.y;
          if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
          geserBaru.current = true;
          geser(dx < 0 ? 1 : -1);
        }}
        /* ⛔⛔ Lihat `geserBaru`: gesekan yang berhenti di atas "Jadi" tak boleh menyatakan hidangan keluar. */
        onClickCapture={(e) => {
          if (!geserBaru.current) return;
          geserBaru.current = false;
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {tampil.map((k) => {
          const age = k.ageSeconds + drift;
          const j = judulKartu(k);
          return (
            <article
              key={k.ticketId}
              className={`kds-ticket ${ageClass(age, thresholds)}`}
              data-testid={`kds-ticket-${k.orderNo}`}
              data-age={ageClass(age, thresholds)}
              /*
               * ⭐⭐ Kertas BERWARNA hanya untuk kartu yang punya tanda; yang tanpa tanda dapat
               * kertas ABU-ABU — tetap memo tempel (bentuk, bayangan, miringnya sama), cuma tak
               * bercerita apa-apa. ⛔ Dulu putih polos, dan itu salah dengan cara yang baru
               * ketahuan dari papan sungguhan: saat SELURUH tiketnya tak bertanda (16 dari 16 di
               * PB-PKB), papan yang seharusnya penuh memo tempel kembali terlihat persis seperti
               * tabel putih yang lama — perubahannya seolah tak pernah dipasang.
               */
              data-note={j.kind === 'none' ? 'plain' : 'paper'}
              style={{
                '--note-tilt': noteTilt(j.kind, j.label),
                ...(j.kind === 'none' ? {} : { '--note-hue': noteHue(j.kind, j.label) }),
              } as CSSProperties}
            >
              <header>
                {/*
                  ⭐⭐⭐ **Jenis penandanya DISEBUT, bukan disiratkan** (Imron, 2026-08-23:
                  *"staff kitchen butuh penanda apakah meja, queue, nama"*).

                  ⛔ Sebelum ini nama tamu tampil telanjang — "Budi" tak bisa dibedakan dari nomor
                  antrean "7" maupun meja "T-07" kalau tamunya kebetulan bernama angka (dan di sini
                  nama MEMANG boleh angka). Juru masak yang salah membaca jenisnya mengantar piring
                  ke tempat yang salah, dan itu kesalahan yang cuma ketahuan dari tamu.
                */}
                <span
                  className={`kds-kind kds-kind-${j.kind}`}
                  data-testid={`kds-kind-${k.orderNo}`}
                >
                  {t(`kds.kind.${j.kind}`)}
                </span>
                <strong data-testid={`kds-judul-${k.orderNo}`}>
                  {t(j.key, { label: j.label })}
                </strong>
                {/* ⭐ Ronde disebut: ronde 2 adalah pesanan SUSULAN meja yang sama, bukan ulangan. */}
                {k.sendRound > 1 ? (
                  <span className="kds-round" data-testid={`kds-round-${k.orderNo}`}>
                    {t('kds.round', { n: k.sendRound })}
                  </span>
                ) : null}
                <span className="kds-age" data-testid={`kds-age-${k.orderNo}`}>{formatAge(age)}</span>
              </header>
              {/*
                ⭐⭐⭐ **Set menu: kepala di atas, komponen menjorok** (Imron, 2026-08-07).
                ⛔ Yang bisa diketuk KOMPONENnya. Kepala bukan hidangan — ia hilang sendiri begitu
                anaknya habis, dan membiarkannya diketuk berarti paket lenyap dari layar sementara
                komponennya masih menggantung.
              */}
              <ul>
                {k.lines.map((l) => (
                  <li
                    key={l.id}
                    className={
                      l.isHeader ? 'kds-head' : (l.parentLineId !== null ? 'kds-child' : undefined)
                    }
                  >
                    {l.isHeader ? (
                      <>
                        <span className="kds-qty">{Math.round(Number(l.qty))}×</span>
                        <span className="kds-name">{l.name}</span>
                      </>
                    ) : (
                      /*
                       * ⚠️ Barisnya SENDIRI yang jadi tombol — sasaran selebar baris. Ikon kecil di
                       * ujungnya akan meleset di tablet dapur yang tangannya basah, dan meleset di
                       * sini berarti hidangan yang belum keluar dinyatakan keluar.
                       */
                      <button
                        type="button"
                        className="kds-line"
                        data-testid={`kds-line-${l.lineId}`}
                        /* ⛔ Elemennya diambil dari peristiwanya, bukan dicari lewat `data-testid`:
                           testid baris memakai `lineId` sementara bump memakai `id`, dan selektor
                           yang meleset menghasilkan bayangan yang diam-diam tak pernah muncul. */
                        onClick={(e) => {
                          lepasBayangan(e.currentTarget);
                          void bumpLine(k.ticketId, l.id);
                        }}
                      >
                        <span className="kds-qty">{Math.round(Number(l.qty))}×</span>
                        <span className="kds-name">{l.name}</span>
                        {l.detail !== null && l.detail !== '' ? (
                          <span className="kds-detail">{l.detail}</span>
                        ) : null}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
              <footer>
                {k.status === 'queued' ? (
                  <button
                    type="button"
                    className="ghost"
                    data-testid={`kds-start-${k.orderNo}`}
                    onClick={() => void act(k.ticketId, 'start', k.orderNo)}
                  >
                    {t('kds.start')}
                  </button>
                ) : (
                  <span className="kds-doing" data-testid={`kds-doing-${k.orderNo}`}>
                    {t('kds.inProgress')}
                  </span>
                )}
                <button
                  type="button"
                  className="primary"
                  data-testid={`kds-bump-${k.orderNo}`}
                  onClick={() => void act(k.ticketId, 'bump', k.orderNo)}
                >
                  {t('kds.bump')}
                </button>
              </footer>
            </article>
          );
        })}
      </div>

      {/*
        ⭐⭐⭐ **Kemudi satu-kartu.** ⛔ Geser saja tak cukup: tangan yang basah atau bersarung tak
        selalu terbaca layar sentuh, dan juru masak yang gesekannya tak menghasilkan apa pun akan
        menyimpulkan layarnya menggantung. Tombolnya sasaran besar dan selalu ada.

        ⚠️ Nomor posisi DISEBUT — tanpanya tak ada cara tahu masih ada berapa di belakang, dan
        kartu terakhir tak bisa dibedakan dari kartu yang kebetulan tak mau bergeser.
      */}
      {single && tickets.length > 0 && (
        <nav className="kds-nav" data-testid="kds-nav">
          <button
            type="button"
            className="ghost"
            data-testid="kds-prev"
            disabled={idx <= 0}
            onClick={() => { geser(-1); }}
          >
            {t('kds.nav.prev')}
          </button>
          <span className="kds-pos" data-testid="kds-pos">
            {t('kds.nav.pos', { i: idx + 1, n: tickets.length })}
          </span>
          <button
            type="button"
            className="ghost"
            data-testid="kds-next"
            disabled={idx >= tickets.length - 1}
            onClick={() => { geser(1); }}
          >
            {t('kds.nav.next')}
          </button>
        </nav>
      )}
    </div>
  );
}
